import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useUpdateRmaItem } from '../../hooks/useRma';
import type { RmaItem, ItemCondition, ItemResolution } from '../../types';

interface InspectItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rmaId: string;
  item: RmaItem;
}

export function InspectItemModal({ open, onOpenChange, rmaId, item }: InspectItemModalProps) {
  const { t } = useTranslation('operations');
  const updateItem = useUpdateRmaItem();

  const [condition, setCondition] = useState<ItemCondition | ''>(item.conditionOnReceipt || '');
  const [resolution, setResolution] = useState<ItemResolution | ''>(item.itemResolution || '');
  const [notes, setNotes] = useState(item.itemNotes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateItem.mutate(
      {
        rmaId,
        itemId: item.id,
        data: {
          conditionOnReceipt: condition || undefined,
          itemResolution: resolution || undefined,
          itemNotes: notes || undefined,
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('rma.inspect.title', 'Inspect Item')}</DialogTitle>
        </DialogHeader>

        {/* Item info */}
        <div className="rounded-md bg-muted/50 p-3 space-y-1 text-sm">
          <div>
            <span className="text-muted-foreground">{t('rma.inspect.sku', 'SKU: ')}</span>
            <span className="font-mono">{item.sku}</span>
          </div>
          {item.productName && (
            <div>
              <span className="text-muted-foreground">{t('rma.inspect.product', 'Product: ')}</span>
              <span>{item.productName}</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t('rma.inspect.condition', 'Condition')}</Label>
            <Select value={condition} onValueChange={(v) => setCondition(v as ItemCondition)}>
              <SelectTrigger>
                <SelectValue placeholder={t('rma.inspect.conditionPlaceholder', 'Select condition...')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="untested">{t('rma.condition.untested', 'Untested')}</SelectItem>
                <SelectItem value="functional">{t('rma.condition.functional', 'Functional')}</SelectItem>
                <SelectItem value="defective">{t('rma.condition.defective', 'Defective')}</SelectItem>
                <SelectItem value="damaged">{t('rma.condition.damaged', 'Damaged')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('rma.inspect.resolution', 'Resolution')}</Label>
            <Select value={resolution} onValueChange={(v) => setResolution(v as ItemResolution)}>
              <SelectTrigger>
                <SelectValue placeholder={t('rma.inspect.resolutionPlaceholder', 'Select resolution...')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="repaired">{t('rma.resolution.repaired', 'Repaired')}</SelectItem>
                <SelectItem value="replaced">{t('rma.resolution.replaced', 'Replaced')}</SelectItem>
                <SelectItem value="scrapped">{t('rma.resolution.scrapped', 'Scrapped')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inspectNotes">{t('rma.inspect.notes', 'Notes')}</Label>
            <Textarea
              id="inspectNotes"
              placeholder={t('rma.inspect.notesPlaceholder', 'Inspection notes...')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('rma.inspect.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={updateItem.isPending}>
              {updateItem.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {t('rma.inspect.save', 'Save Inspection')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
